import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MAX_REWARD_CENTS,
  MAX_REWARD_STARS,
  REPEAT_UNITS,
  isWallTime,
  mondayOf,
  parseAmount,
} from '@kraftwerk/shared';
import type { Repeat, RepeatUnit, TaskListKind } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember, LocalTask, LocalTaskList } from '../../db/local.js';
import { currentHouseholdId, listMembers } from '../../db/shopping.js';
import {
  addTask,
  canUnshare,
  completeTask,
  completionsOf,
  createTaskList,
  datedTasks,
  deleteTask,
  deleteTaskList,
  finishChecklistRun,
  householdDoneThisWeek,
  listTaskLists,
  renameTaskList,
  reopenTask,
  restoreTasks,
  setListHousehold,
  tasksOfList,
  updateTask,
} from '../../db/tasks.js';
import type { DueTask, TaskDraft, TaskView } from '../../db/tasks.js';
import { formatDay } from '../../format.js';
import { useTimeZone, useToday } from '../../hooks/useToday.js';
import { useIsChild, useMayPlan } from '../../hooks/useModules.js';
import { readProfile } from '../../db/profile.js';
import { formatMoney } from '../shopping/Costs.js';
import { SwipeRow, useSwipeBack, useUndo } from '../../gestures/SwipeProvider.js';
import { listInPlanScope } from '../../rooms.js';
import type { PlanScope } from '../../rooms.js';

/**
 * Aufgaben und Checklisten (E12): oben, was fällig ist; darunter die Listen.
 * Eine Liste zeigt ihre Aufgaben (mit Unterpunkten) und auf Wunsch ihre
 * Vergangenheit — was wann von wem erledigt wurde.
 */
export function TasksView({
  focusTaskId,
  scope = 'all',
}: {
  focusTaskId?: string | null;
  /** Räume (27.10.2026): meins = private Listen, unseres = geteilte. */
  scope?: PlanScope;
}): React.JSX.Element {
  const { t } = useTranslation();
  const today = useToday();
  const me = currentOwnerId();
  const mayPlan = useMayPlan();
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const lists = useLiveQuery(() => listTaskLists(db), [], [] as LocalTaskList[]);
  const members = useLiveQuery(
    () => listMembers(householdId, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const due = useLiveQuery(
    () =>
      today ? datedTasks({ me, today, to: addWeek(today) }, db) : Promise.resolve([] as DueTask[]),
    [today, me],
    [] as DueTask[],
  );
  const focusList = useLiveQuery(
    async () => (focusTaskId ? (await db.tasks.get(focusTaskId))?.listId : undefined),
    [focusTaskId],
  );
  const timeZone = useTimeZone();
  // „Diese Woche erledigt“ je Person ist ein Vergleich — Kindern nie gezeigt,
  // auch nicht Teens mit Erfolgen (28.10.2026, keine Ranglisten).
  const child = useIsChild();
  const achievements =
    useLiveQuery(async () => (await readProfile(db)).achievements, [], false) && !child;
  const fairness = useLiveQuery(
    () =>
      today && timeZone
        ? householdDoneThisWeek(mondayOf(today), today, timeZone, db)
        : Promise.resolve(new Map<string, number>()),
    [today, timeZone],
    new Map<string, number>(),
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Eine Aufgabe aus der Mitteilung öffnet ihre Liste in jeder Auswahl.
  const selected = lists.find((list) => list.id === (openId ?? focusList)) ?? null;
  const shownLists = lists.filter((list) => listInPlanScope(list, scope));
  // Fällig ist ohnehin nur, was mich betrifft; „unseres“ zeigt davon das Geteilte.
  const shownDue = scope === 'ours' ? due.filter((task) => task.householdId) : due;

  if (!today) return <></>;

  if (selected) {
    return (
      <ListDetail
        list={selected}
        today={today}
        me={me}
        householdId={householdId}
        members={members}
        onBack={() => setOpenId(null)}
      />
    );
  }

  return (
    <section aria-labelledby="tasks-heading" className="flex flex-col gap-3">
      <h2 id="tasks-heading" className="sr-only">
        {t('tasks.heading')}
      </h2>

      <section aria-labelledby="tasks-due" className="kw-tile flex flex-col gap-1">
        <h3 id="tasks-due" className="kw-kicker">
          {t('tasks.due')}
        </h3>
        {shownDue.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('tasks.nothingDue')}</p>
        ) : (
          <ul className="flex flex-col">
            {shownDue.map((task) => (
              <DueRow key={task.id} task={task} today={today} me={me} />
            ))}
          </ul>
        )}
      </section>

      {fairness.size > 0 && achievements ? (
        <section aria-labelledby="tasks-fairness" className="kw-tile flex flex-col gap-1">
          <h3 id="tasks-fairness" className="kw-kicker">
            {t('tasks.fairness')}
          </h3>
          <p className="text-sm">
            {[...fairness.entries()]
              .map(
                ([userId, count]) =>
                  `${userId === me ? t('householdGoal.me') : (members.find((member) => member.userId === userId)?.displayName ?? t('tasks.someone'))} ${count}`,
              )
              .join(' · ')}
          </p>
        </section>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <h3 className="kw-kicker">{t('tasks.lists')}</h3>
        {mayPlan ? (
          <button
            type="button"
            aria-expanded={creating}
            onClick={() => setCreating(!creating)}
            className="kw-btn kw-btn-small px-3"
          >
            {t('tasks.newList')}
          </button>
        ) : null}
      </div>
      {creating ? (
        <NewList
          householdId={householdId}
          onDone={(id) => {
            setCreating(false);
            if (id) setOpenId(id);
          }}
        />
      ) : null}

      {shownLists.length === 0 ? (
        <p className="kw-tile text-sm text-ink-muted">
          {lists.length === 0 ? t('tasks.noLists') : t('tasks.noListsInScope')}
        </p>
      ) : (
        <ul className="kw-tile flex flex-col divide-y divide-line p-0">
          {shownLists.map((list) => (
            <li key={list.id}>
              <button
                type="button"
                onClick={() => setOpenId(list.id)}
                className="flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{list.name}</span>
                  <span className="block text-xs text-ink-muted">
                    {t(`tasks.kind.${list.kind}`)}
                    {list.householdId ? ` · ${t('tasks.shared')}` : ` · ${t('tasks.private')}`}
                  </span>
                </span>
                <span aria-hidden="true" className="text-ink-muted">
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function addWeek(date: string): string {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 7);
  return next.toISOString().slice(0, 10);
}

function DueRow({
  task,
  today,
  me,
}: {
  task: DueTask;
  today: string;
  me: string | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <li className="flex min-h-11 items-center gap-3">
      <label className="flex size-11 shrink-0 items-center justify-center">
        <input
          type="checkbox"
          checked={false}
          onChange={() => void completeTask(task.id, { me, today }, db)}
          aria-label={t('tasks.completeNamed', { title: task.title })}
          className="size-6 accent-[var(--color-accent)]"
        />
      </label>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{task.title}</span>
        <span
          className={`block truncate text-xs ${task.state === 'overdue' ? 'text-warn' : 'text-ink-muted'}`}
        >
          {task.state === 'overdue'
            ? t('tasks.overdueSince', { date: formatDay(task.dueDate!, 'short', i18n.language) })
            : task.state === 'today'
              ? t('tasks.today')
              : formatDay(task.dueDate!, 'short', i18n.language)}
          {task.dueTime ? ` · ${task.dueTime}` : ''} · {task.listName}
          {task.repeat ? ` · ${t('tasks.repeats')}` : ''}
        </span>
      </span>
    </li>
  );
}

function NewList({
  householdId,
  onDone,
}: {
  householdId: string | null;
  onDone: (id: string | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const nameId = useId();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<TaskListKind>('tasks');
  const [shared, setShared] = useState(false);
  return (
    <form
      className="kw-tile flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === '') return;
        void createTaskList({ name, kind, householdId: shared ? householdId : null }, db).then(
          (list) => onDone(list.id),
        );
      }}
    >
      <label htmlFor={nameId} className="flex flex-col gap-1">
        <span className="kw-label">{t('tasks.listName')}</span>
        <input
          id={nameId}
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
          className="kw-field"
          required
        />
      </label>
      <div role="group" aria-label={t('tasks.kindLabel')} className="flex flex-wrap gap-2">
        {(['tasks', 'checklist'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
            className="kw-toggle"
          >
            {t(`tasks.kind.${option}`)}
          </button>
        ))}
        {householdId ? (
          <button
            type="button"
            aria-pressed={shared}
            onClick={() => setShared(!shared)}
            className="kw-toggle"
          >
            {t('tasks.shareWithHousehold')}
          </button>
        ) : null}
      </div>
      <p className="text-xs text-ink-muted">{t(`tasks.kindWhy.${kind}`)}</p>
      <div className="flex gap-2">
        <button type="submit" className="kw-btn kw-btn-small flex-1">
          {t('tasks.create')}
        </button>
        <button
          type="button"
          onClick={() => onDone(null)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </form>
  );
}

function ListDetail({
  list,
  today,
  me,
  householdId,
  members,
  onBack,
}: {
  list: LocalTaskList;
  today: string;
  me: string | null;
  householdId: string | null;
  members: LocalHouseholdMember[];
  onBack: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  useSwipeBack(onBack);
  // Kleine Kinder (E20): ansehen und eigene Aufgaben abhaken — mehr nicht.
  const mayPlan = useMayPlan();
  const items = useLiveQuery(
    () => tasksOfList(list.id, today, db),
    [list.id, today],
    [] as TaskView[],
  );
  const history = useLiveQuery(() => completionsOf(list.id, db), [list.id], []);
  const unsharable = useLiveQuery(() => canUnshare(list.id, me, db), [list.id, me], false);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState<LocalTask | 'new' | null>(null);
  const [subOf, setSubOf] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState(list.name);
  const nameId = useId();
  const nameOf = (userId: string | null) =>
    members.find((member) => member.userId === userId)?.displayName ?? null;

  if (editing) {
    return (
      <TaskEditor
        list={list}
        task={editing === 'new' ? null : editing}
        parentId={subOf}
        members={members}
        onClose={() => {
          setEditing(null);
          setSubOf(null);
        }}
      />
    );
  }

  const done = items.filter((item) => item.doneAt).length;

  return (
    <section aria-labelledby="list-title" className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
      >
        ← {t('tasks.lists')}
      </button>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id="list-title" className="font-display text-2xl font-bold break-words">
            {list.name}
          </h2>
          <p className="text-xs text-ink-muted">
            {t(`tasks.kind.${list.kind}`)} ·{' '}
            {list.householdId ? t('tasks.shared') : t('tasks.private')}
          </p>
        </div>
        {mayPlan ? (
          <button
            type="button"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
            className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
          >
            {t('tasks.listSettings')}
          </button>
        ) : null}
      </div>

      {menu ? (
        <div className="kw-tile flex flex-col gap-2">
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) void renameTaskList(list.id, name, db);
            }}
          >
            <label htmlFor={nameId} className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="kw-label">{t('tasks.listName')}</span>
              <input
                id={nameId}
                value={name}
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
                className="kw-field"
              />
            </label>
            <button type="submit" className="kw-btn kw-btn-small px-3">
              {t('tasks.rename')}
            </button>
          </form>
          {list.householdId ? (
            unsharable ? (
              <button
                type="button"
                onClick={() => void setListHousehold(list.id, null, db)}
                className="kw-btn kw-btn-quiet kw-btn-small"
              >
                {t('tasks.makePrivate')}
              </button>
            ) : (
              <p className="text-xs text-ink-muted">{t('tasks.cannotUnshare')}</p>
            )
          ) : householdId ? (
            <button
              type="button"
              onClick={() => void setListHousehold(list.id, householdId, db)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('tasks.shareWithHousehold')}
            </button>
          ) : null}
          {confirmDelete ? (
            <div className="flex flex-col gap-2 rounded-xl border border-danger p-3">
              <p className="text-sm">{t('tasks.deleteWarning')}</p>
              <button
                type="button"
                onClick={() => void deleteTaskList(list.id, db).then(onBack)}
                className="kw-btn kw-btn-small border-danger text-danger"
              >
                {t('tasks.deleteList')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('tasks.deleteList')}
            </button>
          )}
        </div>
      ) : null}

      <ul className="kw-tile flex flex-col gap-1">
        {items.length === 0 ? (
          <li className="text-sm text-ink-muted">{t('tasks.emptyList')}</li>
        ) : null}
        {items.map((item) => (
          <li key={item.id} className="flex flex-col">
            <TaskRow
              task={item}
              today={today}
              me={me}
              assignee={nameOf(item.assigneeId)}
              canCheck={mayPlan || item.assigneeId === me}
              onEdit={mayPlan ? () => setEditing(item) : undefined}
            />
            {item.children.length > 0 ? (
              <ul className="ml-8 flex flex-col border-l border-line pl-2">
                {item.children.map((child) => (
                  <li key={child.id}>
                    <TaskRow
                      task={child}
                      today={today}
                      me={me}
                      assignee={nameOf(child.assigneeId)}
                      canCheck={mayPlan || child.assigneeId === me}
                      onEdit={mayPlan ? () => setEditing(child) : undefined}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            {!item.doneAt && mayPlan ? (
              <button
                type="button"
                onClick={() => {
                  setSubOf(item.id);
                  setEditing('new');
                }}
                className="ml-9 self-start text-xs text-accent underline"
              >
                {t('tasks.addSub')}
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      <div className={mayPlan ? 'flex gap-2' : 'hidden'}>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="kw-btn kw-btn-small flex-1"
        >
          {list.kind === 'checklist' ? t('tasks.addItem') : t('tasks.addTask')}
        </button>
        {list.kind === 'checklist' && items.length > 0 ? (
          <button
            type="button"
            onClick={() => void finishChecklistRun(list.id, me, db)}
            className="kw-btn kw-btn-quiet kw-btn-small flex-1"
          >
            {t('tasks.finishRun', { done, total: items.length })}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        aria-expanded={showHistory}
        onClick={() => setShowHistory(!showHistory)}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {showHistory ? t('tasks.hideHistory') : t('tasks.showHistory', { count: history.length })}
      </button>
      {showHistory ? (
        <ul className="kw-tile flex flex-col gap-1 text-sm">
          {history.length === 0 ? <li className="text-ink-muted">{t('tasks.noHistory')}</li> : null}
          {history.map((entry) => (
            <li key={entry.id} className="flex justify-between gap-2">
              <span className="min-w-0 truncate">
                {entry.title}
                {entry.itemsTotal !== null
                  ? ` · ${t('tasks.runResult', { done: entry.itemsDone ?? 0, total: entry.itemsTotal })}`
                  : ''}
              </span>
              <span className="shrink-0 text-xs text-ink-muted">
                {formatDay(entry.doneAt.slice(0, 10), 'short', i18n.language)}
                {nameOf(entry.doneBy) ? ` · ${nameOf(entry.doneBy)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function TaskRow({
  task,
  today,
  me,
  assignee,
  canCheck,
  onEdit,
}: {
  task: LocalTask & { state: string };
  today: string;
  me: string | null;
  assignee: string | null;
  /** Kleine Kinder (E20) haken nur eigene Aufgaben ab. */
  canCheck: boolean;
  /** Fehlt bei kleinen Kindern — dann nur lesen. */
  onEdit?: (() => void) | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const done = task.doneAt !== null;
  const showUndo = useUndo();
  const toggle = () =>
    void (done ? reopenTask(task.id, db) : completeTask(task.id, { me, today }, db));
  // Wischen (24.10.2026): nach rechts erledigt, nach links löschen mit
  // „Rückgängig“ — nur, wer auch Häkchen bzw. Bearbeiten darf.
  return (
    <SwipeRow
      as="div"
      className="flex min-h-11 items-center gap-3"
      right={
        canCheck
          ? { label: done ? t('gestures.uncheck') : t('gestures.done'), tone: 'good', run: toggle }
          : null
      }
      left={
        onEdit
          ? {
              label: t('gestures.delete'),
              tone: 'danger',
              run: () =>
                void deleteTask(task.id, db).then((rows) =>
                  showUndo(
                    t('gestures.deleted', { name: task.title }),
                    () => void restoreTasks(rows, db),
                  ),
                ),
            }
          : null
      }
    >
      <label className="flex size-11 shrink-0 items-center justify-center">
        <input
          type="checkbox"
          checked={done}
          disabled={!canCheck}
          onChange={toggle}
          aria-label={
            done
              ? t('tasks.reopenNamed', { title: task.title })
              : t('tasks.completeNamed', { title: task.title })
          }
          className="size-6 accent-[var(--color-accent)]"
        />
      </label>
      <button
        type="button"
        onClick={onEdit}
        disabled={!onEdit}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span className={`block truncate text-sm ${done ? 'text-ink-muted line-through' : ''}`}>
          {task.title}
        </span>
        {task.dueDate || assignee || task.repeat || task.rewardCents || task.rewardStars ? (
          <span
            className={`block truncate text-xs ${task.state === 'overdue' ? 'text-warn' : 'text-ink-muted'}`}
          >
            {task.dueDate ? formatDay(task.dueDate, 'short', i18n.language) : ''}
            {task.dueTime ? ` · ${task.dueTime}` : ''}
            {task.repeat
              ? ` · ${t(`tasks.every.${task.repeat.unit}`, { count: task.repeat.every })}`
              : ''}
            {assignee ? ` · ${assignee}` : ''}
            {task.rewardCents
              ? ` · ${t('tasks.rewardShort', { amount: formatMoney(task.rewardCents, i18n.language) })}`
              : ''}
            {task.rewardStars ? ` · ${t('tasks.starsShort', { count: task.rewardStars })}` : ''}
          </span>
        ) : null}
      </button>
    </SwipeRow>
  );
}

function TaskEditor({
  list,
  task,
  parentId,
  members,
  onClose,
}: {
  list: LocalTaskList;
  task: LocalTask | null;
  parentId: string | null;
  members: LocalHouseholdMember[];
  onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = {
    title: useId(),
    date: useId(),
    time: useId(),
    every: useId(),
    unit: useId(),
    assignee: useId(),
    reward: useId(),
    note: useId(),
  };
  const [title, setTitle] = useState(task?.title ?? '');
  const [dueDate, setDueDate] = useState(task?.dueDate ?? '');
  const [dueTime, setDueTime] = useState(task?.dueTime ?? '');
  const [repeats, setRepeats] = useState(task?.repeat !== null && task?.repeat !== undefined);
  const [every, setEvery] = useState(String(task?.repeat?.every ?? 1));
  const [unit, setUnit] = useState<RepeatUnit>(task?.repeat?.unit ?? 'week');
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? '');
  const [note, setNote] = useState(task?.note ?? '');
  const [reward, setReward] = useState(
    task?.rewardCents ? (task.rewardCents / 100).toFixed(2).replace('.', ',') : '',
  );
  const [rewardStars, setRewardStars] = useState(task?.rewardStars ?? 0);
  const checklist = list.kind === 'checklist';
  // Geld fürs Erledigen (E20): nur Erwachsene, nur geteilte Listen, und nur,
  // wenn es bei mindestens einem Kind des Haushalts eingeschaltet ist.
  const me = currentOwnerId();
  const showReward =
    !checklist &&
    list.householdId !== null &&
    members.some((member) => member.userId === me && member.role !== 'child') &&
    members.some((member) => member.role === 'child' && member.taskRewards);
  // Sterne (26.10.2026): dieselben Grenzen, eingeschaltet je Kind.
  const showStars =
    !checklist &&
    list.householdId !== null &&
    members.some((member) => member.userId === me && member.role !== 'child') &&
    members.some((member) => member.role === 'child' && member.taskStars);
  const rewardCents = reward.trim() === '' ? null : parseAmount(reward);
  const rewardInvalid =
    reward.trim() !== '' &&
    (rewardCents === null || rewardCents <= 0 || rewardCents > MAX_REWARD_CENTS);

  const repeat: Repeat | null =
    repeats && dueDate && Number.isInteger(Number(every)) && Number(every) >= 1
      ? { every: Number(every), unit }
      : null;
  const draft: TaskDraft = {
    title,
    note: note || null,
    dueDate: checklist ? null : dueDate || null,
    dueTime: !checklist && dueDate && isWallTime(dueTime) ? dueTime : null,
    repeat: checklist ? null : repeat,
    assigneeId: list.householdId ? assigneeId || null : null,
    parentId: task ? task.parentId : parentId,
    ...(showReward && !rewardInvalid ? { rewardCents } : {}),
    ...(showStars ? { rewardStars: rewardStars || null } : {}),
  };

  return (
    <form
      aria-labelledby="task-editor-heading"
      className="kw-tile flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim() === '' || (showReward && rewardInvalid)) return;
        const save = task ? updateTask(task.id, draft, db) : addTask(list.id, draft, db);
        void save.then(onClose);
      }}
    >
      <h2 id="task-editor-heading" className="font-display text-xl font-bold">
        {task ? t('tasks.editTask') : parentId ? t('tasks.addSub') : t('tasks.addTask')}
      </h2>
      <label htmlFor={ids.title} className="flex flex-col gap-1">
        <span className="kw-label">{t('tasks.title')}</span>
        <input
          id={ids.title}
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          className="kw-field"
          required
        />
      </label>

      {!checklist ? (
        <>
          <div className="flex flex-wrap gap-2">
            <label htmlFor={ids.date} className="flex flex-col gap-1">
              <span className="kw-label">{t('tasks.dueDate')}</span>
              <input
                id={ids.date}
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="kw-field"
              />
            </label>
            {dueDate ? (
              <label htmlFor={ids.time} className="flex flex-col gap-1">
                <span className="kw-label">{t('tasks.dueTime')}</span>
                <input
                  id={ids.time}
                  type="time"
                  value={dueTime}
                  onChange={(event) => setDueTime(event.target.value)}
                  className="kw-field"
                />
              </label>
            ) : null}
          </div>
          {dueDate ? <p className="text-xs text-ink-muted">{t('tasks.timeWhy')}</p> : null}
          {dueDate ? (
            <div className="flex flex-wrap items-end gap-2">
              <button
                type="button"
                aria-pressed={repeats}
                onClick={() => setRepeats(!repeats)}
                className="kw-toggle"
              >
                {t('tasks.repeat')}
              </button>
              {repeats ? (
                <>
                  <label htmlFor={ids.every} className="flex w-20 flex-col gap-1">
                    <span className="kw-label">{t('tasks.everyLabel')}</span>
                    <input
                      id={ids.every}
                      inputMode="numeric"
                      value={every}
                      onChange={(event) => setEvery(event.target.value)}
                      className="kw-field"
                    />
                  </label>
                  <label htmlFor={ids.unit} className="flex flex-col gap-1">
                    <span className="kw-label">{t('tasks.unitLabel')}</span>
                    <select
                      id={ids.unit}
                      value={unit}
                      onChange={(event) => setUnit(event.target.value as RepeatUnit)}
                      className="kw-field"
                    >
                      {REPEAT_UNITS.map((option) => (
                        <option key={option} value={option}>
                          {t(`tasks.unit.${option}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {list.householdId && members.length > 0 ? (
        <label htmlFor={ids.assignee} className="flex flex-col gap-1">
          <span className="kw-label">{t('tasks.assignee')}</span>
          <select
            id={ids.assignee}
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            className="kw-field"
          >
            <option value="">{t('tasks.nobody')}</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.displayName}
              </option>
            ))}
          </select>
          <span className="text-xs text-ink-muted">{t('tasks.assigneeWhy')}</span>
        </label>
      ) : null}

      {showReward ? (
        <label htmlFor={ids.reward} className="flex flex-col gap-1">
          <span className="kw-label">{t('tasks.reward')}</span>
          <input
            id={ids.reward}
            inputMode="decimal"
            value={reward}
            onChange={(event) => setReward(event.target.value)}
            placeholder="0,50"
            aria-invalid={rewardInvalid}
            className="kw-field w-32"
          />
          {rewardInvalid ? (
            <span role="alert" className="text-xs text-danger">
              {t('tasks.rewardInvalid', {
                max: formatMoney(MAX_REWARD_CENTS, i18n.language),
              })}
            </span>
          ) : (
            <span className="text-xs text-ink-muted">{t('tasks.rewardWhy')}</span>
          )}
        </label>
      ) : null}

      {showStars ? (
        <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
          <legend className="kw-label">{t('tasks.stars')}</legend>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: MAX_REWARD_STARS + 1 }, (_, count) => (
              <button
                key={count}
                type="button"
                aria-pressed={rewardStars === count}
                onClick={() => setRewardStars(count)}
                aria-label={count === 0 ? t('tasks.starsNone') : t('tasks.starsShort', { count })}
                className="kw-toggle"
              >
                {count === 0 ? t('tasks.starsNone') : `${count} ★`}
              </button>
            ))}
          </div>
          <span className="text-xs text-ink-muted">{t('tasks.starsWhy')}</span>
        </fieldset>
      ) : null}

      <label htmlFor={ids.note} className="flex flex-col gap-1">
        <span className="kw-label">{t('tasks.note')}</span>
        <textarea
          id={ids.note}
          value={note}
          rows={2}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          className="kw-field"
        />
      </label>

      <div className="flex gap-2">
        <button type="submit" className="kw-btn flex-1">
          {t('tasks.save')}
        </button>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet flex-1">
          {t('nutrition.library.cancel')}
        </button>
      </div>
      {task ? (
        <button
          type="button"
          onClick={() => void deleteTask(task.id, db).then(onClose)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('tasks.deleteTask')}
        </button>
      ) : null}
    </form>
  );
}
